import { Button, Divider, Grid2, Stack, Typography, alpha } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { colors } from 'src/global/styles/colors';
import { useGetPricesQuery } from 'src/store/services/price.service';
import { useAppSelector } from 'src/store/utils';
import { formatNumWithDots } from 'src/utils/helpers';

interface CurrencySelectionProps {
  nodeName?: string;
  readOnly?: boolean;
  token?: string;
  amountNode?: number;
  pricePerNode?: number;
}

const CurrencySelection = (props: CurrencySelectionProps) => {
  const { nodeName, readOnly, token, amountNode = 0, pricePerNode = 0 } = props;
  const { networkInfo } = useAppSelector(state => state.app);
  const { setValue } = useFormContext();
  const { data: prices } = useGetPricesQuery();

  const totalPrice = amountNode * pricePerNode;

  const getTokenPrice = (token: string) => {
    if (!prices || !prices.decimals || !prices.prices[token]) return 0;
    return (totalPrice * 10 ** (prices.decimals || 8)) / prices.prices[token] || 0;
  };

  const renderTokens = () => (
    <div className='col-span-12'>
      <Grid2 container spacing={2}>
        {networkInfo?.tokens?.map((item, index) => {
          const isSelect = item.symbol === token;
          return (
            <Grid2 size={3} key={item.symbol}>
              <Button
                key={index}
                variant='outlined'
                color='primary'
                onClick={() => !readOnly && setValue('token', item.symbol)}
                sx={{
                  width: '100%',
                  color: 'white',
                  borderColor: isSelect ? 'transparent' : `1px solid ${alpha('rgb(255, 255, 255)', 0.15)}`,
                  borderRadius: '4px',
                  backgroundColor: isSelect ? colors.brand300 : 'transparent',
                  padding: '8px 16px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '16px',
                }}>
                <img width={24} height={24} src={item.image} alt={item.symbol} />
                {item.name?.toLocaleUpperCase()}
              </Button>
            </Grid2>
          );
        })}
      </Grid2>
    </div>
  );

  const renderTotal = () => (
    <div className='rounded-lg py-4 px-6 bg-neutral-03 col-span-12'>
      <Grid2 container spacing={2} columns={12}>
        <Grid2 size={5}>
          <Typography className='text-body_m text-neutral-90 font-gotham'>NFT</Typography>
        </Grid2>
        <Grid2 size={3}>
          <Typography textAlign={'center'} className='text-body_m text-neutral-90 font-gotham'>
            Quantity
          </Typography>
        </Grid2>
        <Grid2 size={4}>
          <Typography textAlign={'right'} className='text-body_m text-neutral-90 font-gotham'>
            Total Price
          </Typography>
        </Grid2>
      </Grid2>
      <Divider className='border-neutral-15' sx={{ my: 1, borderColor: 'white' }} />
      <Grid2 container spacing={2} columns={12}>
        <Grid2 size={5}>
          <Typography className='text-body_m text-white font-gotham'>{nodeName || '-'}</Typography>
        </Grid2>
        <Grid2 size={3}>
          <Typography textAlign={'center'} className='text-body_m text-white font-gotham'>
            {amountNode || 0}
          </Typography>
        </Grid2>
        <Grid2 size={4}>
          <Stack gap={1} justifyContent={'end'}>
            <Typography textAlign={'right'} fontWeight={500} className='text-head_xs text-white font-gotham'>
              USD ${formatNumWithDots(totalPrice) || '0.0'}
            </Typography>
            <Typography
              gap={1}
              justifyContent={'end'}
              display={'flex'}
              alignItems={'center'}
              className='text-body_m text-neutral-90 font-gotham'>
              <img
                width={16}
                height={16}
                src={networkInfo?.tokens?.find(item => item.symbol === token)?.image}
                alt={networkInfo?.tokens?.find(item => item.symbol === token)?.name}
              />{' '}
              {formatNumWithDots(getTokenPrice(token || 'usdt')) || '0.0'}
            </Typography>
          </Stack>
        </Grid2>
      </Grid2>
    </div>
  );

  return (
    <>
      <Typography className='text-head_s font-gotham text-white col-span-12'>Currency Selection (ERC-20)</Typography>
      <Typography className='text-body_m font-gotham text-white col-span-12'>
        Choose your preferred currency. It will be the default on your NFT page.
      </Typography>
      {renderTokens()}
      {renderTotal()}
    </>
  );
};

export default CurrencySelection;
